import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { RemotionGraphic } from '../components/graphics/RemotionGraphics';

const { FiHeart, FiHome, FiUsers, FiCoffee, FiArrowRight, FiPhone, FiSun } = FiIcons;

const Home = () => {
  const services = [
    {
      graphic: "service-personal",
      title: "Personal Care at Home",
      description: "Gentle, respectful personal care that helps you maintain your independence and dignity in your own home."
    },
    {
      graphic: "service-companion",
      title: "Live-in Companions",
      description: "Caring companions who become part of your daily life, providing support with warmth and understanding."
    },
    {
      graphic: "service-family",
      title: "Family-Centered Care",
      description: "We work closely with your family to ensure care that reflects your values, preferences, and lifestyle."
    },
    {
      graphic: "service-daily",
      title: "Daily Life Support",
      description: "From morning coffee to evening routines, we're here to help with the daily activities that matter most."
    }
  ];

  return (
    <>
      <Helmet>
        <title>KCG | Home Care and Support | Kabod Care Group</title>
        <meta name="description" content="Warm, compassionate home care services that feel like having family nearby. Personal care, companionship, and support in the comfort of your own home." />
        <meta name="keywords" content="home care, personal care, companionship, elderly care, family care, community care, home support" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-50 via-secondary-50 to-primary-100 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-100/30 to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div className="space-y-6">
                <div className="flex items-center space-x-3 text-primary-900">
                  <SafeIcon icon={FiSun} className="w-6 h-6" />
                  <span className="font-medium">Care That Feels Like Home</span>
                </div>
                <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Stay Home,
                  <span className="text-primary-900 block">Stay Happy,</span>
                  <span className="text-gray-700">Stay You</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Warm, compassionate care that helps you live independently in the place you love most - your own home. 
                  Our caring team becomes part of your daily life, not just your care routine.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/request-care"
                  className="inline-flex items-center justify-center bg-primary-900 text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-primary-800 transition-colors duration-200 group shadow-lg"
                >
                  Start Your Care Journey
                  <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center border-2 border-primary-900 text-primary-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-primary-900 hover:text-white transition-colors duration-200"
                >
                  <SafeIcon icon={FiPhone} className="mr-2 w-5 h-5" />
                  Chat With Us
                </Link>
              </div>

            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl border border-primary-100 bg-primary-50">
                <RemotionGraphic
                  variant="hero"
                  className="w-full aspect-[4/3]"
                  ariaLabel="Animated illustration of an older person and carer sharing tea at home"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Care That Fits Your Life</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We believe care should feel natural and comfortable, fitting seamlessly into your daily routine 
              and honoring the way you've always lived your life.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-primary-50 to-secondary-50 p-8 rounded-2xl hover:shadow-xl transition-all duration-300 group border border-primary-100"
              >
                <div className="w-16 h-16 rounded-2xl overflow-hidden mb-6 shadow-sm ring-1 ring-primary-100">
                  <RemotionGraphic
                    variant={service.graphic}
                    className="w-full h-full"
                    ariaLabel={`${service.title} animated care graphic`}
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link
              to="/services"
              className="inline-flex items-center text-primary-900 hover:text-primary-700 font-semibold text-lg group"
            >
              Explore All Our Services
              <SafeIcon icon={FiArrowRight} className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 to-secondary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <RemotionGraphic
                variant="why"
                className="rounded-3xl shadow-2xl overflow-hidden w-full aspect-[4/3] border border-primary-100 bg-white"
                ariaLabel="Animated kitchen and community care illustration"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Why Families Choose Us</h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  We're not just another care company. We're neighbors who understand that home is where the heart is, 
                  and we're here to help you stay exactly where you belong.
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <SafeIcon icon={FiHome} className="w-4 h-4 text-primary-900" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Local & Personal</h3>
                    <p className="text-gray-600">We're part of your community, understanding local needs and building genuine relationships that last.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <SafeIcon icon={FiUsers} className="w-4 h-4 text-primary-900" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Carefully Matched Carers</h3>
                    <p className="text-gray-600">We take time to match you with carers who share your interests, values, and personality - like finding a new friend.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <SafeIcon icon={FiHeart} className="w-4 h-4 text-primary-900" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Family-Focused Approach</h3>
                    <p className="text-gray-600">Your family is part of our care team. We keep everyone connected and involved in your wellbeing.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <SafeIcon icon={FiCoffee} className="w-4 h-4 text-primary-900" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Life Enhancement</h3>
                    <p className="text-gray-600">Beyond basic care, we focus on joy, comfort, and the little things that make each day brighter.</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-900 to-secondary-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <RemotionGraphic
              variant="cta"
              className="w-full max-w-4xl mx-auto aspect-[900/260]"
              ariaLabel="Animated care consultation journey from home to phone call"
            />
            <h2 className="text-4xl font-bold text-white mb-4">Let's Start Your Story Together</h2>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto">
              Every great care relationship starts with a simple conversation. Let's chat about how we can help 
              you or your loved one live happily and independently at home.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/request-care"
                className="inline-flex items-center justify-center bg-white text-primary-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-primary-50 transition-colors duration-200 shadow-lg"
              >
                Talk to Our Team
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-4 rounded-full text-lg font-semibold hover:bg-white hover:text-primary-900 transition-colors duration-200"
              >
                <SafeIcon icon={FiPhone} className="mr-2 w-5 h-5" />
                Call Us Today
              </Link>
            </div>
            <p className="text-primary-100 text-sm">
              Tell us what support you need and where care is required.
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;
